import { useParams, Link } from 'react-router-dom';
import SEOHelmet from '@/components/feature/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/data';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const seoData = {
    title: `${article.title} - 777影院`,
    description: article.excerpt,
    keywords: `${article.category},777影院,影视资讯,电影文章,电影推荐`,
    canonicalUrl: `https://777cinema.com/article/${article.id}`,
    ogType: 'article',
    ogImage: article.coverImage,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.coverImage,
      datePublished: article.date,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      publisher: {
        '@type': 'Organization',
        name: '777影院',
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://777cinema.com/' },
      { '@type': 'ListItem', position: 2, name: '影视资讯', item: 'https://777cinema.com/article/1' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHelmet data={seoData} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article>
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb items={[
                { label: '首页', to: '/' },
                { label: '影视资讯', to: '/article/1' },
                { label: article.title },
              ]} />
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-medium text-primary-500 bg-primary-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>
              <h1 className="text-xl md:text-3xl font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次阅读
                </span>
              </div>
            </div>

            <div className="relative w-full aspect-[16/9] mb-8 overflow-hidden rounded-lg">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">标签：</span>
                <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">{article.category}</span>
                <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">777影院</span>
                <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">影视资讯</span>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-lg font-bold text-foreground-950 mb-6">相关文章</h2>
              <div className="flex flex-col gap-4">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}